import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import "./i18n";
import "./index.css";
import App from "./App";
import { initDataSource } from "./lib/dataSource";
import { flush as flushListenQueue } from "./lib/listenQueue";
import { initInstallEventTracking } from "./lib/installEvent";
import { initSentry } from "./lib/sentry";
import { reportIfReloadedWhileFullscreenOpen } from "./lib/chapterSceneFullscreenDiagnostics";

// Register the service worker (autoUpdate) — best effort, never blocks
// first render. Not registered at all outside a secure context / when
// unsupported (e.g. some in-app browsers).
if ("serviceWorker" in navigator) {
  void import("virtual:pwa-register").then(({ registerSW }) => {
    registerSW({ immediate: true });
  }).catch(() => {
    /* PWA plugin not active in this build mode — fine, app still works */
  });
}

initInstallEventTracking();

// Chouette is an app, not a web page: no native pinch or double-tap zoom
// anywhere (product decision, 2026-09-27). The viewport meta in index.html
// says so, but iOS Safari ignores user-scalable=no in some versions, so its
// non-standard gesture events are cancelled here too. The chapter
// illustration's full-screen view keeps its own pointer-driven zoom.
for (const type of ["gesturestart", "gesturechange", "gestureend"]) {
  document.addEventListener(type, (event) => event.preventDefault(), { passive: false });
}

// Listen-event queue (plan Lot C.2): drain whatever a previous, offline
// session queued as soon as there's a connection again — at startup, and on
// every `online` transition.
window.addEventListener("online", () => { void flushListenQueue(); });

// Fire-and-forget, no-ops without VITE_SENTRY_DSN (local dev) — see
// lib/sentry.ts's module docstring.
initSentry();

// iPhone-only bug report evidence (see chapterSceneFullscreenDiagnostics.ts):
// must run before anything else reads/writes its sessionStorage flag.
reportIfReloadedWhileFullscreenOpen();

// Probe for a real backend before the first render decides mock vs. real
// (see dataSource.ts) — bounded to 2s, so startup is never blocked long.
void initDataSource().finally(() => {
  void flushListenQueue();
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );
});
